/**
 * Venue partner routes. Owner: venue portal.
 * Functions (not constants) so flag() is read on every render.
 *
 * SLOT_MODEL (Phase 8B) swaps the venue portal to the rebuilt slot pages, all lazy and behind
 * RequireVenue: /venue/dashboard (VenueSlotDashboard), /venue/settings (VenueSlotSettings,
 * which pulls in zod + components/portal/form, so it must stay lazy) and the new
 * /venue/deliveries (VenueDeliveries: shipments to confirm, coasters placed per month; flag on
 * only). The retired pages redirect (replace): /venue/coasters to /venue/deliveries and
 * /venue/analytics to /venue/dashboard (whatever VENUE_ANALYTICS says). The slot pages frame
 * themselves with PortalShell (no legacy VenueHeader). Site redesign Phase 8 swaps
 * /venue/advertisers too (VenueSlotAdvertisers: the businesses guests see at the venue, read from
 * its public deals page, GET /venues/{location_id}/deals-page, in that page's order; the venue
 * slot dashboard gives only the venue id, the capacity and the nav badge count). With the flag
 * off nothing changes: /venue/deliveries is not declared (it falls to the public catch-all,
 * NotFound with the site chrome), and the rest is exactly as before.
 *
 * Site redesign Phase 5: with SLOT_MODEL on, /venue/apply is the redesigned public
 * application (pages/public/VenueApplySlot, mounted by routes/public.jsx under PublicLayout);
 * the old account-first VenueApplication is declared here only with the flag off.
 */
import React from "react";
import { Navigate, Route } from "react-router-dom";
import { flag } from "../config";
import { RequireVenue } from "../components/routing/guards";
import lazyPortalPage from "./lazyPortalPage";
import NotFound from "../pages/NotFound";
import VenueApplication from "../pages/venue/VenueApplication";
import VenueDashboard from "../pages/venue/VenueDashboard";
import VenueSettings from "../pages/venue/VenueSettings";
import VenueLogin from "../pages/venue/VenueLogin";
import CoasterManagement from "../pages/venue/CoasterManagement";
import VenueAnalytics from "../pages/venue/VenueAnalytics";
import VenueAdvertisers from "../pages/venue/VenueAdvertisers";

// Phase 8B slot-model venue pages (SLOT_MODEL). VenueSlotSettings imports zod and
// components/portal/form (lazy-only); the other two are lazy for chunk size.
const VenueSlotDashboard = lazyPortalPage("VenueSlotDashboard", () => import("../pages/venue/slot/VenueSlotDashboard"));
const VenueDeliveries = lazyPortalPage("VenueDeliveries", () => import("../pages/venue/slot/VenueDeliveries"));
const VenueSlotSettings = lazyPortalPage("VenueSlotSettings", () => import("../pages/venue/slot/VenueSlotSettings"));
// Site redesign Phase 8: the advertisers at the venue (lazy for chunk size).
const VenueSlotAdvertisers = lazyPortalPage("VenueSlotAdvertisers", () => import("../pages/venue/slot/VenueAdvertisers"));

/** Venue pages that show the public site chrome (PublicLayout). */
export function venuePublicRoutes() {
  return (
    <>
      <Route path="/venue/login" element={<VenueLogin />} />
    </>
  );
}

/** Venue portal (PortalLayout, no site chrome). */
export function venuePortalRoutes() {
  const slot = flag('SLOT_MODEL');
  return (
    <>
      {!slot && <Route path="/venue/apply" element={<VenueApplication />} />}
      <Route element={<RequireVenue />}>
        <Route path="/venue/dashboard" element={slot ? <VenueSlotDashboard /> : <VenueDashboard />} />
        <Route path="/venue/settings" element={slot ? <VenueSlotSettings /> : <VenueSettings />} />
        <Route
          path="/venue/coasters"
          element={slot ? <Navigate to="/venue/deliveries" replace /> : <CoasterManagement />}
        />
        {slot && <Route path="/venue/deliveries" element={<VenueDeliveries />} />}
        {slot && <Route path="/venue/analytics" element={<Navigate to="/venue/dashboard" replace />} />}
        {!slot && flag('VENUE_ANALYTICS') && <Route path="/venue/analytics" element={<VenueAnalytics />} />}
        <Route path="/venue/advertisers" element={slot ? <VenueSlotAdvertisers /> : <VenueAdvertisers />} />
      </Route>
      {/* Flags off: still a portal path, so NotFound renders without site chrome. */}
      {!slot && !flag('VENUE_ANALYTICS') && <Route path="/venue/analytics" element={<NotFound />} />}
    </>
  );
}
